// src/pages/staff/components/StaffExportModal.tsx

import React, { useState } from 'react';
import {
  Modal,
  Radio,
  Form,
  Input,
  Typography,
  Space,
  Alert,
  Divider,
  Card,
  Checkbox,
  message,
} from 'antd';
import {
  ExportOutlined,
  FileTextOutlined,
  TableOutlined,
  DatabaseOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import type { StaffMember } from '../../../types/api.types';
import staffExportService from '../../../services/staff-export.service';

const { Title, Text } = Typography;

interface StaffExportModalProps {
  open: boolean;
  onClose: () => void;
  staff: StaffMember[];
}

type ExportFormat = 'csv' | 'json' | 'permissions' | 'stats';

interface ExportOptions {
  format: ExportFormat;
  filename: string;
  includeInactive: boolean;
  includePermissions: boolean;
  includeSalary: boolean;
  includeNotes: boolean;
  customFilename: boolean;
}

const StaffExportModal: React.FC<StaffExportModalProps> = ({
  open,
  onClose,
  staff,
}) => {
  const [form] = Form.useForm();
  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'csv',
    filename: '',
    includeInactive: true,
    includePermissions: false,
    includeSalary: false,
    includeNotes: true,
    customFilename: false,
  });

  const formatOptions = [
    {
      value: 'csv' as const,
      label: 'CSV (Excel)',
      description: 'Format standard pour tableurs (Excel, Google Sheets)',
      icon: <TableOutlined style={{ color: '#52c41a' }} />,
      recommended: true,
    },
    {
      value: 'json' as const,
      label: 'JSON',
      description: 'Format technique pour développeurs et intégrations',
      icon: <DatabaseOutlined style={{ color: '#1890ff' }} />,
      recommended: false,
    },
    {
      value: 'permissions' as const,
      label: 'Rapport des permissions',
      description: 'Export détaillé des permissions par membre',
      icon: <SafetyOutlined style={{ color: '#722ed1' }} />,
      recommended: false,
    },
    {
      value: 'stats' as const,
      label: 'Statistiques',
      description: 'Rapport statistique sur l\'équipe',
      icon: <FileTextOutlined style={{ color: '#fa8c16' }} />,
      recommended: false,
    },
  ];

  const getDefaultFilename = (format: ExportFormat): string => {
    const date = new Date().toISOString().split('T')[0];
    const baseName = `personnel_avec_${date}`;
    
    switch (format) {
      case 'csv': return `${baseName}.csv`;
      case 'json': return `${baseName}.json`;
      case 'permissions': return `permissions_${baseName}.csv`;
      case 'stats': return `statistiques_${baseName}.json`;
      default: return `${baseName}.csv`;
    }
  };

  const handleFormatChange = (format: ExportFormat) => {
    const newOptions = {
      ...exportOptions,
      format,
      filename: exportOptions.customFilename ? exportOptions.filename : getDefaultFilename(format),
    };
    setExportOptions(newOptions);
    form.setFieldsValue(newOptions);
  };

  const handleExport = async () => {
    try {
      const values = await form.validateFields();
      setIsExporting(true);

      // Filtrer les données selon les options
      let filteredStaff = staff;
      if (!values.includeInactive) {
        filteredStaff = staff.filter(s => s.isActive);
      }

      // Valider les données avant export
      const validation = staffExportService.validateExportData(filteredStaff);
      if (!validation.isValid) {
        message.error(`Erreur de validation: ${validation.errors.join(', ')}`);
        return;
      }

      // Exporter selon le format choisi
      switch (values.format) {
        case 'csv':
          staffExportService.exportToCSV(filteredStaff, values.filename);
          break;
        case 'json':
          staffExportService.exportToJSON(filteredStaff, values.filename);
          break;
        case 'permissions':
          staffExportService.exportPermissionsReport(filteredStaff, values.filename);
          break;
        case 'stats':
          staffExportService.exportStatsReport(filteredStaff, values.filename);
          break;
        default:
          throw new Error('Format d\'export non supporté');
      }

      message.success(`Export ${values.format.toUpperCase()} réussi!`);
      onClose();
    } catch (error: any) {
      console.error('Erreur lors de l\'export:', error);
      message.error(error.message || 'Échec de l\'export');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setExportOptions({
      format: 'csv',
      filename: '',
      includeInactive: true,
      includePermissions: false,
      includeSalary: false,
      includeNotes: true,
      customFilename: false,
    });
    onClose();
  };

  const activeStaffCount = staff.filter(s => s.isActive).length;
  const inactiveStaffCount = staff.length - activeStaffCount;

  return (
    <Modal
      title={
        <Space>
          <ExportOutlined style={{ color: '#52c41a' }} />
          <span>Exporter les données du personnel</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      onOk={handleExport}
      confirmLoading={isExporting}
      width={600}
      okText={isExporting ? 'Export en cours...' : 'Exporter'}
      cancelText="Annuler"
      destroyOnClose
    >
      <Alert
        message="Information sur l'export"
        description={`Vous allez exporter les données de ${staff.length} membre(s) du personnel (${activeStaffCount} actif(s), ${inactiveStaffCount} inactif(s)).`}
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={exportOptions}
        requiredMark={false}
      >
        {/* Choix du format */}
        <Form.Item name="format" label="Format d'export">
          <Radio.Group
            onChange={(e) => handleFormatChange(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {formatOptions.map(option => (
                <Card
                  key={option.value}
                  size="small"
                  style={{
                    cursor: 'pointer',
                    border: exportOptions.format === option.value ? '2px solid #52c41a' : '1px solid #d9d9d9',
                  }}
                  onClick={() => handleFormatChange(option.value)}
                >
                  <Radio value={option.value} style={{ width: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        {option.icon}
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.label}
                            {option.recommended && (
                              <Text type="success" style={{ marginLeft: '8px', fontSize: '12px' }}>
                                (Recommandé)
                              </Text>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {option.description}
                          </div>
                        </div>
                      </Space>
                    </Space>
                  </Radio>
                </Card>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>

        <Divider />

        {/* Options d'export */}
        <Title level={5} style={{ marginBottom: '12px' }}>
          Options d'export
        </Title>

        <Form.Item name="includeInactive" valuePropName="checked">
          <Checkbox>
            Inclure le personnel inactif ({inactiveStaffCount} membre(s))
          </Checkbox>
        </Form.Item>

        {exportOptions.format === 'csv' && (
          <>
            <Form.Item name="includePermissions" valuePropName="checked">
              <Checkbox>
                Inclure les détails des permissions
              </Checkbox>
            </Form.Item>

            <Form.Item name="includeSalary" valuePropName="checked">
              <Checkbox>
                Inclure les informations de salaire
              </Checkbox>
            </Form.Item>

            <Form.Item name="includeNotes" valuePropName="checked">
              <Checkbox>
                Inclure les notes et commentaires
              </Checkbox>
            </Form.Item>
          </>
        )}

        <Divider />

        {/* Nom du fichier */}
        <Form.Item name="customFilename" valuePropName="checked">
          <Checkbox onChange={(e) => setExportOptions(prev => ({ ...prev, customFilename: e.target.checked }))}>
            Nom de fichier personnalisé
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="filename"
          label="Nom du fichier"
          rules={[
            { required: true, message: 'Le nom du fichier est requis' },
            { 
              pattern: /^[^<>:"/\\|?*]+$/, 
              message: 'Nom de fichier invalide' 
            },
          ]}
        >
          <Input
            placeholder={getDefaultFilename(exportOptions.format)}
            disabled={!exportOptions.customFilename}
            suffix={
              exportOptions.format === 'json' || exportOptions.format === 'stats' ? '.json' : '.csv'
            }
          />
        </Form.Item>

        <Alert
          message="Note de confidentialité"
          description="Les données exportées peuvent contenir des informations sensibles. Assurez-vous de les traiter conformément aux politiques de confidentialité de votre organisation."
          type="warning"
          showIcon
          style={{ marginTop: '16px' }}
        />
      </Form>
    </Modal>
  );
};

export default StaffExportModal;